import React, { useEffect, useState } from 'react';
import { Popup } from 'react-map-gl';
import { getDaysSince } from '../utilities/time';
import { getGenderText } from '../utilities/misc';

interface IAnimalPopupProps {
  selected: any;
  species: any;
  expanded: any;
  anchor?: 'top' | 'bottom' | 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
  offset?: number;
  onClose: any;
  onDrawClick: any;
  onExitPath?: any;
}

const configValue: string = (process.env.REACT_APP_biotracker_service as string);

function resolveAssetUrl(path?: string) {
  if (!path) {
    return '';
  }

  if (path.startsWith('http://') || path.startsWith('https://') || path.startsWith('data:')) {
    return path;
  }

  return `${configValue}${path}`;
}

function getPopupImage(species: any) {
  return resolveAssetUrl(species?.imageUrl || species?.thumbnailUrl || species?.image || species?.thumbnail || '');
}

function getSpeciesLabel(species: any, selected: any) {
  return selected?.species || species?.name || '';
}

function AnimalPopupMobileCard(props: {
  selected: any;
  species: any;
  detail: any;
  popupImage: string;
  popupImageLoading: boolean;
  historyLoading: boolean;
  onDrawClick: any;
  onExitPath?: any;
  desktop?: boolean;
  expanded?: boolean;
}) {
  const [distance, setDistance] = useState<number | null>(null);
  const [duration, setDuration] = useState(0);
  const [lastPing, setLastPing] = useState(0);
  const [gender, setGender] = useState('Unknown');
  const [more, setMore] = useState(false);

  useEffect(() => {
    setGender(getGenderText(props.selected));
    setLastPing(getDaysSince(props.selected));
    setDuration(getDaysSince(props.selected.tagged));
    if (!props.detail) {
      setDistance(null);
      return;
    }

    setDistance(props.detail.travelDistanceKm ?? null);
  }, [props.selected, props.detail]);

  const lastPingText = lastPing > 365
    ? `${Math.floor(lastPing / 365)} year${lastPing > 730 ? 's' : ''} ago`
    : `${lastPing} days ago`;

  const avgSpeed = distance !== null && duration > lastPing
    ? (distance / (duration - lastPing) / 24).toFixed(1)
    : null;
  const speciesLabel = getSpeciesLabel(props.species, props.selected);
  const showHeaderSpecies = !props.expanded;
  const metaLines = [
    props.selected.sponsor ? `Sponsored by: ${props.selected.sponsor}` : null,
    props.selected.tagOwner ? `Project Leader: ${props.selected.tagOwner}` : null,
  ].filter(Boolean);

  const isHover = props.desktop && !props.expanded;

  const headerStyle = props.popupImage
    ? { backgroundImage: `url("${props.popupImage}")`,
        WebkitMaskImage: isHover ? 'none' : 'linear-gradient(to bottom, black 70%, transparent 95%)',
        maskImage: isHover ? 'none' : 'linear-gradient(to bottom, black 70%, transparent 95%)'
      }
    : undefined;

  const cardClass = props.desktop && props.expanded ? 'apm-card apm-card--popup apm-card--popup-expanded'
    : isHover ? 'apm-card apm-card--popup apm-card--popup-hover'
    : 'apm-card';

  const showSize = props.selected.size && props.selected.size !== 'Unknown' && props.selected.size !== 0;
  const showGender = gender && gender !== 'Unknown';
  const subText = [showGender ? gender : null, showSize ? `${props.selected.size}cm` : null].filter(Boolean).join(', ');

  return (
    <div className={cardClass}>
      {(props.popupImage || (props.popupImageLoading && !isHover)) ? (
        <div className="apm-header">
          <div className={props.popupImage ? 'apm-header-image' : 'apm-header-image apm-header-image--loading'} style={headerStyle}></div>
          <div className="apm-identity">
            <span className="apm-name">{props.selected.name}</span>
            {showHeaderSpecies && speciesLabel && <span className="apm-species">{speciesLabel}</span>}
            {subText && <span className="apm-sub">{subText}</span>}
            <span className="apm-ping">Last Ping: {lastPingText}</span>
          </div>
        </div>
      ) : (
        <div className="apm-identity">
          <span className="apm-name">{props.selected.name}</span>
          {showHeaderSpecies && speciesLabel && <span className="apm-species">{speciesLabel}</span>}
          {subText && <span className="apm-sub">{subText}</span>}
          <span className="apm-ping">Last Ping: {lastPingText}</span>
        </div>
      )}
      {!(props.desktop && !props.expanded) && (
        <div className="apm-body">
          {metaLines.length > 0 && (
            <div className="apm-sponsor">
              {metaLines.map((line) => (
                <div key={line}>{line}</div>
              ))}
            </div>
          )}
          <div className="apm-stats">
            <div className="apm-stat">
              <span className="apm-stat-label">TRACKED</span>
              <span className="apm-stat-value">{duration}<small>d</small></span>
            </div>
            <div className="apm-stat">
              <span className="apm-stat-label">DISTANCE</span>
              <span className="apm-stat-value">{distance === null ? '--' : distance.toFixed(0)}{distance === null ? null : <small>kms</small>}</span>
            </div>
            <div className="apm-stat">
              <span className="apm-stat-label">AVG. SPEED</span>
              <span className="apm-stat-value">{avgSpeed ?? '--'}{avgSpeed === null ? null : <small>km/h</small>}</span>
            </div>
          </div>
          <div className="apm-footer">
            {speciesLabel && (
              <div className="apm-footer-row">
                <span>{speciesLabel}</span>
                {props.species?.scientificName && <span className="apm-scientific">{props.species.scientificName}</span>}
              </div>
            )}
            {/* UNCOMMENT TO SHOW IUCN STATUS */}
            {/* <div className="apm-footer-row">
              <span>{props.species && !props.species.protected ? 'Not ' : ''}Protected</span>
              <span>IUCN: {getProtectedStatusText(props.species && props.species.status)}</span>
            </div> */}
          </div>
          {props.species && props.species.description && (
            <span
              onClick={() => setMore(!more)}
              className="apm-show-more">
              Show {more ? 'less' : 'more'} on species
            </span>
          )}
          {more && props.species && props.species.description && (
            <p className="apm-description">{props.species.description}</p>
          )}
          {props.onExitPath ? (
            <button className="apm-button apm-button--primary" style={{ marginBottom: '10px' }} onClick={props.onExitPath}>
              Exit Tracking History
            </button>
          ) : (
            <button className="apm-button apm-button--primary" onClick={props.onDrawClick} disabled={props.historyLoading}>
              {props.historyLoading ? 'Loading Tracking History...' : 'See Tracking History'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default function AnimalPopup(props: IAnimalPopupProps) {

  const [detail, setDetail] = useState<any>(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [resolvedPopupImage, setResolvedPopupImage] = useState('');
  const [popupImageLoading, setPopupImageLoading] = useState(false);
  const isMobilePortrait = window.innerWidth <= 768 && window.innerHeight > window.innerWidth;
  const popupImage = getPopupImage(props.species);

  useEffect(() => {
    if (!popupImage) {
      setResolvedPopupImage('');
      setPopupImageLoading(false);
      return;
    }

    let cancelled = false;
    const image = new Image();
    setResolvedPopupImage('');
    setPopupImageLoading(true);

    image.onload = () => {
      if (cancelled) {
        return;
      }

      setResolvedPopupImage(popupImage);
      setPopupImageLoading(false);
    };

    image.onerror = () => {
      if (cancelled) {
        return;
      }

      setResolvedPopupImage('');
      setPopupImageLoading(false);
    };

    image.src = popupImage;

    return () => {
      cancelled = true;
    };
  }, [popupImage]);

  useEffect(() => {
    setResolvedPopupImage('');
    setPopupImageLoading(false);
  }, [props.selected?.tagId, props.expanded]);

  useEffect(() => {
    if (!props.expanded) {
      setDetail(null);
      return;
    }

    const controller = new AbortController();
    let cancelled = false;

    (async () => {
      try {
        const resp = await fetch(`${configValue}/api/v1/animals/${props.selected.tagId}/popup`, {
          signal: controller.signal,
        });
        if (!resp.ok) {
          throw new Error(`Request failed with status ${resp.status}`);
        }

        const data = await resp.json();
        if (!cancelled) {
          setDetail(data);
        }
      } catch (err: any) {
        if (err?.name === 'AbortError') {
          return;
        }

        console.error('Failed to load animal popup details', err);
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
      setDetail(null);
    };
  }, [props.expanded, props.selected?.tagId]);

  useEffect(() => {
    setHistoryLoading(false);
  }, [props.selected?.tagId, props.expanded]);

  async function handleDrawClick() {
    if (historyLoading) {
      return;
    }

    setHistoryLoading(true);

    try {
      const resp = await fetch(`${configValue}/api/v1/animals/${props.selected.tagId}`);
      if (!resp.ok) {
        throw new Error(`Request failed with status ${resp.status}`);
      }

      const data = await resp.json();
      props.onDrawClick(data);
    } catch (err) {
      console.error('Failed to load animal tracking history', err);
    } finally {
      setHistoryLoading(false);
    }
  }

  if (isMobilePortrait) {
    return (
      <div className="apm-shell">
        <AnimalPopupMobileCard
          selected={props.selected}
          species={props.species}
          detail={detail}
          popupImage={resolvedPopupImage}
          popupImageLoading={popupImageLoading}
          historyLoading={historyLoading}
          onDrawClick={handleDrawClick}
          onExitPath={props.onExitPath}
          expanded={props.expanded}
        />
      </div>
    );
  }

  return (
    <Popup
      className={props.expanded ? 'animal-popup animal-popup--expanded' : 'animal-popup animal-popup--hover'}
      anchor={props.anchor ?? 'bottom'}
      offset={props.offset ?? 20}
      longitude={Number(props.selected.position.longitude)}
      latitude={Number(props.selected.position.latitude)}
      onClose={props.onClose}
      closeOnClick={false}
      closeButton={props.expanded}
      maxWidth="460px"
    >
      <AnimalPopupMobileCard
        selected={props.selected}
        species={props.species}
        detail={detail}
        popupImage={resolvedPopupImage}
        popupImageLoading={popupImageLoading}
        historyLoading={historyLoading}
        onDrawClick={handleDrawClick}
        desktop={true}
        expanded={props.expanded}
      />
    </Popup>
  )
}